import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConnectDomainField } from '../../components/ConnectDomainField';
import { DnsTable } from '../../components/DnsTable';
import { DnsRecordList } from '../../components/DnsRecordList';
import { PencilIcon, ChevronDownIcon } from '../../components/icons';
import { useCustomizer } from '../../store/customizer';
import {
  DesktopBody,
  Card,
  Section,
  Label,
  Divider,
  LinkBtn,
  LinkBtnLabel,
  ErrorNote,
  MobileErrorNote,
  ChosenField,
  ChosenValue,
  EditBtn,
  Heading,
  Body,
  ToggleBtn,
  ToggleLabel,
  Accordion,
  AccordionInner,
  ConnectFooterBar,
  ProviderBtn,
  ProviderBtnLabel,
  EntriLogo,
  ENTRI_LOGO,
  MobileBody,
  MobileHeading,
  MobileSubtext,
  MobileSettingsNote,
  MobileSectionLabel,
  MobileDnsToggle,
  MobileDnsChevron,
  MobileField,
  MobileEntriBtn,
} from './connect.styles';

/** Copy a value to the clipboard (best-effort; the icons are the affordance). */
const copyToClipboard = (value: string): void => {
  void navigator.clipboard?.writeText(value).catch(() => undefined);
};

/* ── Desktop connect-domain flow (input → DNS records) ───────────────────── */

export const DesktopConnect = (): React.ReactElement => {
  const { t } = useTranslation();
  const {
    connectStage,
    connectDomain,
    setConnectDomain,
    connectDomainValid,
    submitConnectDomain,
    editConnectDomain,
    connectLoading,
    connectError,
    dnsRecords,
    connectProvider,
    connectAutoSupported,
    openEntri,
    entriOpen,
    selectedName,
    selectedDomain,
  } = useCustomizer();
  // DNS records start COLLAPSED; "View DNS Settings" expands them (accordion).
  const [dnsOpen, setDnsOpen] = useState(false);

  // ── Input screen ──────────────────────────────────────────────────────────
  // The "Link your domain" CTA lives in the card (it fetches the DNS config).
  if (connectStage === 'input') {
    const ready = connectDomainValid && !connectLoading;
    return (
      <DesktopBody>
        <Card>
          <Section>
            <Label
              text="What is the domain you want to link to {{brand}}?"
              placeholders={{ brand: selectedName ?? t('your casino') }}
              tag="span"
            />
            <ConnectDomainField
              variant="desktop"
              value={connectDomain}
              onChange={setConnectDomain}
              onSubmit={submitConnectDomain}
              valid={connectDomainValid}
            />
            {connectError === 'already-used' ? (
              <ErrorNote
                text="This domain is already used by another Whitelabels.com instance and can't be connected. Contact support@whitelabels.com for further assistance."
                tag="paragraph"
              />
            ) : null}
            {connectError === 'blocked' ? (
              <ErrorNote text="This domain can't be used. Please enter a domain you own." tag="paragraph" />
            ) : null}
            <Divider />
            <LinkBtn type="button" $enabled={ready} disabled={!ready} onClick={ready ? submitConnectDomain : undefined}>
              <LinkBtnLabel
                $enabled={ready}
                text={connectLoading ? t('Checking the available options to link your domain …') : t('Link your domain')}
                tag="span"
              />
            </LinkBtn>
          </Section>
        </Card>
      </DesktopBody>
    );
  }

  // ── DNS records screen ─────────────────────────────────────────────────────
  // Records start collapsed; "View DNS Settings" expands the table (accordion).
  return (
    <DesktopBody>
      <Card>
        <Section>
          <Label text="Chosen domain to link" tag="span" />
          <ChosenField>
            <ChosenValue text={selectedDomain ?? connectDomain} tag="span" title={selectedDomain ?? connectDomain} />
            <EditBtn type="button" aria-label={t('Edit domain')} onClick={editConnectDomain}>
              <PencilIcon size={16} />
            </EditBtn>
          </ChosenField>
        </Section>
        <Divider />
        <Section>
          {connectAutoSupported ? (
            <>
              <Heading
                text="We can set up your {{provider}} domain for you automatically."
                placeholders={{ provider: connectProvider ?? t('registrar') }}
                tag="span"
              />
              <Body
                text="You'll sign in to your account on a secure {{provider}} page and approve the connection. We never see your password. This process does not transfer your domain. It stays in your {{provider}} account, fully yours."
                placeholders={{ provider: connectProvider ?? t('registrar') }}
                tag="paragraph"
              />
            </>
          ) : null}
          <ToggleBtn type="button" onClick={() => setDnsOpen((o) => !o)} aria-expanded={dnsOpen}>
            <ToggleLabel text={dnsOpen ? t('Hide DNS Settings') : t('View DNS Settings')} tag="span" />
          </ToggleBtn>
          <Accordion $open={dnsOpen}>
            <AccordionInner>
              <DnsTable records={dnsRecords} onCopy={copyToClipboard} />
            </AccordionInner>
          </Accordion>
        </Section>
        {/* The Entri auto-setup CTA is always offered on the DNS-records screen
            (both auto-supported and manual-setup registrars); Entri's modal
            handles the manual case too, and the DNS records stay as fallback. */}
        <ConnectFooterBar>
          <ProviderBtn type="button" disabled={entriOpen} onClick={openEntri}>
            {entriOpen ? (
              <ProviderBtnLabel text="Connecting …" tag="span" />
            ) : (
              <>
                <ProviderBtnLabel text="Connect with" tag="span" />
                <EntriLogo src={ENTRI_LOGO} alt="Entri" />
              </>
            )}
          </ProviderBtn>
        </ConnectFooterBar>
      </Card>
    </DesktopBody>
  );
};

/* ── Mobile connect-domain flow (input → DNS records) ────────────────────── */

export const MobileConnect = (): React.ReactElement => {
  const { t } = useTranslation();
  const {
    connectStage,
    connectDomain,
    setConnectDomain,
    connectDomainValid,
    submitConnectDomain,
    connectError,
    dnsRecords,
    selectedDomain,
    openEntri,
    entriOpen,
  } = useCustomizer();
  // Records start collapsed; tapping "Required DNS records" expands them (accordion).
  const [dnsOpen, setDnsOpen] = useState(false);

  if (connectStage === 'input') {
    return (
      <MobileBody>
        <MobileHeading text="Connect your own domain" tag="h1" />
        <MobileSubtext text="What is the domain you want to connect?" tag="paragraph" />
        <MobileField>
          <ConnectDomainField
            variant="mobile"
            value={connectDomain}
            onChange={setConnectDomain}
            onSubmit={submitConnectDomain}
            valid={connectDomainValid}
            placeholder={t('Enter your domain')}
          />
        </MobileField>
        {connectError === 'already-used' ? (
          <MobileErrorNote
            text="This domain is already used by another Whitelabels.com instance and can't be connected. Contact support@whitelabels.com for further assistance."
            tag="paragraph"
          />
        ) : null}
        {connectError === 'blocked' ? (
          <MobileErrorNote text="This domain can't be used. Please enter a domain you own." tag="paragraph" />
        ) : null}
      </MobileBody>
    );
  }

  return (
    <MobileBody>
      <MobileHeading text="Connect your own domain" tag="h1" />
      <MobileField>
        <ConnectDomainField variant="mobile" value={selectedDomain ?? connectDomain} valid readOnly />
      </MobileField>
      <MobileSubtext text="Use the following configuration to connect your domain with your casino on Whitelabels.com. Domain propagation can take anywhere from 5 mins to 48 hours." tag="paragraph" />
      <MobileSettingsNote text="You'll find the DNS configuration any time in your Settings area." tag="paragraph" />
      <MobileDnsToggle type="button" onClick={() => setDnsOpen((o) => !o)} aria-expanded={dnsOpen}>
        <MobileSectionLabel text="Required DNS records" tag="span" />
        <MobileDnsChevron $open={dnsOpen}>
          <ChevronDownIcon size={18} />
        </MobileDnsChevron>
      </MobileDnsToggle>
      <Accordion $open={dnsOpen}>
        <AccordionInner>
          <DnsRecordList records={dnsRecords} onCopy={copyToClipboard} />
        </AccordionInner>
      </Accordion>
      {/* Entri one-click auto-setup; "Continue to Logo" (fixed footer) unlocks
          only after it reports success. */}
      <MobileEntriBtn type="button" disabled={entriOpen} onClick={openEntri}>
        {entriOpen ? (
          <ProviderBtnLabel text="Connecting …" tag="span" />
        ) : (
          <>
            <ProviderBtnLabel text="Connect with" tag="span" />
            <EntriLogo src={ENTRI_LOGO} alt="Entri" />
          </>
        )}
      </MobileEntriBtn>
    </MobileBody>
  );
};